import clsx from "clsx";
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  DirectionalHint,
  Icon,
  IconButton,
  Label,
  Link,
  Persona,
  PersonaPresence,
  PersonaSize,
  PrimaryButton,
} from "@fluentui/react";
import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import "@cyntler/react-doc-viewer/dist/index.css";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { NoData } from "../../../../../components/NoData";
import { Flex } from "../../../../../components/styled";
import { useSelector } from "../../../../../store/hooks";
import { setDeleteEvaluationDialog } from "../../../../../store/recommendationPage";
import { InsurerEvaluationWithAttachments } from "../../../../../types/types";
import {
  formatDateString,
  getFileExtension,
  getFileIcon,
  getIconColor,
} from "../../../../../utils/utils";
import { StatusGrecoCode } from "./StatusGrecoCode";
import styled from "styled-components";
import { TooltipComponent } from "components/TooltipComponent";
import { formatDateStringWithTime } from "utils/date";

type Props = {
  evaluation: InsurerEvaluationWithAttachments;
};

export const Evaluation = ({ evaluation: r }: Props) => {
  const { t } = useTranslation();
  const taxonomy = useSelector((s) => s.taxonomy);

  const userRole = useSelector((s) => s.userRole.userRole);
  const dispatch = useDispatch();
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [activeDocUri, setActiveDocUri] = useState<string | undefined>();
  const docViewerContainerRef = useRef<HTMLDivElement>(null);

  // Debug: Track events when scrolling
  useEffect(() => {
    if (!isPreviewDialogOpen) return;

    const parentContainer = document.getElementById("recomendation-form-wrap");
    if (parentContainer) {
      const originalOverflow = parentContainer.style.overflow;
      const originalPosition = parentContainer.style.position;
      parentContainer.style.overflow = "hidden";
      parentContainer.style.position = "fixed";

      // Also prevent scroll event from doing anything
      const preventScroll = (e: Event) => {
        if (e.target === parentContainer) {
          e.preventDefault();
          e.stopPropagation();
          parentContainer.scrollTop = 0;
        }
      };
      parentContainer.addEventListener("scroll", preventScroll, {
        capture: true,
      });

      return () => {
        parentContainer.style.overflow = originalOverflow;
        parentContainer.style.position = originalPosition;
        parentContainer.removeEventListener("scroll", preventScroll, {
          capture: true,
        });
      };
    }
  }, [isPreviewDialogOpen]);

  // Debug event tracker
  useEffect(() => {
    if (!isPreviewDialogOpen) return;

    const logEvent = (eventName: string) => (e: Event) => {
      const target = e.target as HTMLElement;
      console.log(`[DocViewer Event] ${eventName}:`, {
        target: target?.tagName,
        className: target?.className,
        id: target?.id,
        type: e.type,
        timeStamp: e.timeStamp,
      });
    };

    const events = [
      "scroll",
      "wheel",
      "mousedown",
      "mouseup",
      "touchstart",
      "touchmove",
      "touchend",
    ];

    // Listen on document to capture all events
    events.forEach((eventName) => {
      document.addEventListener(eventName, logEvent(eventName), {
        capture: true,
        passive: true,
      });
    });

    // Also track any DOM mutations
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.removedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              console.log("[DocViewer MutationObserver] Node removed:", {
                tagName: node.tagName,
                className: node.className,
                id: node.id,
              });
            }
          });
          mutation.addedNodes.forEach((node) => {
            if (node instanceof HTMLElement) {
              console.log("[DocViewer MutationObserver] Node added:", {
                tagName: node.tagName,
                className: node.className,
                id: node.id,
              });
            }
          });
        } else if (mutation.type === "attributes") {
          console.log("[DocViewer MutationObserver] Attribute changed:", {
            target: (mutation.target as HTMLElement)?.tagName,
            attributeName: mutation.attributeName,
            oldValue: mutation.oldValue,
          });
        }
      });
    });

    // Start observing the DocViewer container
    const container = docViewerContainerRef.current;
    if (container) {
      observer.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeOldValue: true,
      });
    }

    return () => {
      events.forEach((eventName) => {
        document.removeEventListener(eventName, logEvent(eventName), {
          capture: true,
        });
      });
      observer.disconnect();
    };
  }, [isPreviewDialogOpen]);

  const documents = useMemo(() => {
    return (
      r.insurerEvaluationAttachments
        ?.filter((att) => att.attachmentPath)
        .map((att) => ({
          uri: att.attachmentPath as string,
          fileName: att.attachmentName,
          fileType: att.attachmentMimeType || undefined,
        })) || []
    );
  }, [r.insurerEvaluationAttachments]);

  const uploadedAtByUri = useMemo(() => {
    const map = new Map<string, string>();
    r.insurerEvaluationAttachments
      ?.filter((att) => att.attachmentPath)
      .forEach((att) => {
        if (att.attachmentCreatedAt) {
          map.set(att.attachmentPath as string, String(att.attachmentCreatedAt));
        }
      });
    return map;
  }, [r.insurerEvaluationAttachments]);

  const handleDocumentChange = useCallback(
    (doc: { uri: string }) => setActiveDocUri(doc.uri),
    [],
  );

  const docViewerConfig = useMemo(
    () => ({
      header: { disableHeader: false, disableFileName: false },
      pdfZoom: { defaultZoom: 1, zoomJump: 0.1 },
      pdfVerticalScrollByDefault: true,
    }),
    [],
  );

  const docViewerElement = useMemo(
    () => (
      <DocViewer
        key={documents.map((d) => d.uri).join(",")}
        documents={documents}
        pluginRenderers={DocViewerRenderers}
        prefetchMethod="GET"
        onDocumentChange={handleDocumentChange}
        config={docViewerConfig}
      />
    ),
    [documents, handleDocumentChange, docViewerConfig],
  );

  const dialogContentProps = {
    type: DialogType.normal,
    title: t("ram.responsesPage.attachments.previewDocuments"),
    closeButtonAriaLabel: "Close",
  };

  return (
    <Root
      className={clsx({
        draft: true,
        "not-draft": false,
      })}
    >
      <Persona
        title={
          (r.insurerEvaluation.evaluationCreatedByUserName ?? "") +
          "\n" +
          t("ram.clientsList.lastModifiedAt") +
          " " +
          formatDateStringWithTime(r.insurerEvaluation.evaluationLastModifiedAt)
        }
        text={r.insurerEvaluation.evaluationCreatedByUserName ?? ""}
        presence={PersonaPresence.online}
        styles={{
          root: {
            width: "40px",
            alignSelf: "flex-start",
            marginTop: "10px",
          },
        }}
        size={PersonaSize.size40}
      />
      <Bubble>
        <Top>
          <BubbleWrap>
            <div>
              <Label>{t("ram.evaluationsPage.dateOfEvaluation")}</Label>
              {r.insurerEvaluation.dateOfEvaluation &&
                formatDateString(r.insurerEvaluation.dateOfEvaluation)}
            </div>
            <div>
              <Label>{t("ram.evaluationsPage.plannedimplementation")}</Label>
              <div>
                {r.insurerEvaluation.implementationDateInsurer ? (
                  formatDateString(
                    r.insurerEvaluation.implementationDateInsurer,
                  )
                ) : (
                  <NoData />
                )}
              </div>
            </div>
            <StatusGrecoCode
              statusGrECo={r.insurerEvaluation.statusGrEcoCode}
            />
          </BubbleWrap>

          <Flex direction="column" gap={5}>
            <Flex
              direction="column"
              style={{
                visibility: userRole === "admin" ? "visible" : "hidden",
              }}
            >
              <PrimaryButton
                text={t("greco.delete")}
                iconProps={{ iconName: "Trash" }}
                onClick={() => {
                  dispatch(
                    setDeleteEvaluationDialog({
                      isOpen: true,
                      evaluation: r.insurerEvaluation,
                    }),
                  );
                }}
              />
            </Flex>
          </Flex>
        </Top>
        <Middle>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}
          >
            <Label>{t("ram.evaluationsPage.priorityCode.label")}</Label>
            {r.insurerEvaluation.priorityCode ? (
              <span>
                {t(
                  taxonomy.RiskPriority.byId[r.insurerEvaluation.priorityCode]
                    ?.code,
                )}
              </span>
            ) : (
              <NoData />
            )}
          </div>
        </Middle>

        <div
          style={{
            paddingTop: "10px",
          }}
        >
          <div
            style={{
              paddingTop: "10px",
            }}
          >
            <Label>{t("ram.responsesPage.comment")}</Label>
            <div
              dangerouslySetInnerHTML={{ __html: r.insurerEvaluation.comment }}
            />
          </div>
          {r.insurerEvaluationAttachments.length !== 0 && (
            <div>
              <AttachmentLabelRow>
                <Label>{t("ram.responsesPage.attachments")}</Label>
                {documents.length > 0 && (
                  <PreviewIconButton
                    iconProps={{ iconName: "View" }}
                    title={t("ram.responsesPage.attachments.previewDocuments")}
                    ariaLabel={t(
                      "ram.responsesPage.attachments.previewDocuments",
                    )}
                    onClick={() => {
                      setActiveDocUri(documents[0]?.uri);
                      setIsPreviewDialogOpen(true);
                    }}
                  />
                )}
              </AttachmentLabelRow>
              {r.insurerEvaluationAttachments?.map((a) => {
                return (
                  <TooltipComponent
                    key={a.attachmentId}
                    content={a.attachmentName}
                    directionalHint={DirectionalHint.leftTopEdge}
                  >
                    <div
                      key={a.attachmentId}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginLeft: "10px",
                        marginBottom: "10px",
                      }}
                    >
                      <Icon
                        iconName={getFileIcon(
                          getFileExtension(a.attachmentName),
                        )}
                        style={{
                          color: getIconColor(
                            getFileExtension(a.attachmentName),
                          ),
                        }}
                        styles={{
                          root: {
                            marginRight: "5px",
                            fontSize: "14px",
                          },
                        }}
                      ></Icon>
                      <Link
                        styles={{
                          root: {
                            fontSize: " 14px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            flex: 1,
                          },
                        }}
                        onClick={() => {
                          window.open(a.attachmentPath);
                        }}
                      >
                        {a.attachmentName}
                      </Link>
                    </div>
                  </TooltipComponent>
                );
              })}
            </div>
          )}

          <Dialog
            hidden={!isPreviewDialogOpen}
            onDismiss={() => setIsPreviewDialogOpen(false)}
            dialogContentProps={dialogContentProps}
            minWidth="80vw"
            maxWidth="90vw"
            modalProps={{
              isBlocking: false,
              styles: { main: { maxWidth: "90vw" } },
            }}
          >
            <div style={{ display: "flex", alignItems: "center", marginBottom: "10px", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <Label style={{ margin: 0 }}>{t("ram.evaluationsPage.dateOfEvaluation")}:</Label>
                <span>
                  {r.insurerEvaluation.dateOfEvaluation
                    ? formatDateString(r.insurerEvaluation.dateOfEvaluation)
                    : ""}
                </span>
              </div>
              {activeDocUri && uploadedAtByUri.get(activeDocUri) && (
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <Label style={{ margin: 0 }}>
                    {t("ram.responsesPage.attachments.uploadedAt")}:
                  </Label>
                  <span>
                    {formatDateStringWithTime(uploadedAtByUri.get(activeDocUri)!)}
                  </span>
                </div>
              )}
            </div>
            <DocViewerContainer
              ref={docViewerContainerRef}
              onScroll={(e) => e.stopPropagation()}
              onWheel={(e) => e.stopPropagation()}
            >
              {docViewerElement}
            </DocViewerContainer>
            <DialogFooter>
              <DefaultButton
                onClick={() => setIsPreviewDialogOpen(false)}
                text={t("ram.reportDocumentDialogDownloadClose.label")}
              />
            </DialogFooter>
          </Dialog>
        </div>
      </Bubble>
    </Root>
  );
};

const Top = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${(p) => p.theme.palette.neutralPrimary};
  padding-bottom: 10px;
  @media (max-width: 700px) {
    padding-bottom: 0;
  }
`;

const Middle = styled.div`
  padding: 10px 0;
  border-bottom: 1px solid ${(p) => p.theme.palette.neutralPrimary};
`;

const BubbleWrap = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  flex: 1;
  gap: 10px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    > *:last-child {
      margin-bottom: 10px;
    }
  }
`;

export const Bubble = styled.div`
  padding: 15px 20px;
  border: 1px solid ${(p) => p.theme.palette.neutralPrimary};
  max-width: 600px;
  flex: 1;
  background-color: ${(p) => p.theme.palette.neutralLight};
`;

export const Root = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  padding: 0 15px;
  gap: 10px;
  font-size: 14px;
  background-color: ${(p) => p.theme.palette.white};
  color: ${(p) => p.theme.palette.neutralPrimary};
  @media (max-width: 500px) {
    flex-direction: column;
    &&.draft {
      flex-direction: column;
    }
  }
  &.not-draft {
    justify-content: flex-start;
    ${Bubble} {
      border-radius: 0px 40px 40px 40px;
    }
  }
  &.draft {
    justify-content: end;
    flex-direction: row-reverse;
    ${Bubble} {
      justify-self: flex-end;
      border-radius: 40px 0px 40px 40px;
    }
  }
`;

const AttachmentLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PreviewIconButton = styled(IconButton)`
  height: 24px;
  width: 24px;

  i {
    font-size: 14px;
  }
`;

const DocViewerContainer = styled.div`
  width: 100%;
  height: 80vh;
  min-height: 600px;
  overflow: auto;
  overscroll-behavior: contain;

  #react-doc-viewer {
    height: 100% !important;
    overscroll-behavior: contain;
  }

  #proxy-renderer {
    height: 100% !important;
    overscroll-behavior: contain;
  }

  #msdoc-renderer,
  #image-renderer,
  #pdf-renderer {
    height: 100% !important;
    overscroll-behavior: contain;
  }

  .react-pdf__Page__textContent {
    overscroll-behavior: contain;
  }

  iframe {
    height: 100% !important;
    min-height: 550px;
  }
`;
